import { FolderCog, FolderPlus } from "lucide-react";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useNavigate, useParams } from "react-router-dom";

import { useGetChatByUuid } from "@/api/chat/useGetChatByUuid";
import { useChatFolderList } from "@/api/chat-folder/useChatFolderList";
import { useCreateChatFolder } from "@/api/chat-folder/useCreateChatFolder";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { PATHS } from "@/constants/paths";

import { ChatFolderListItem } from "./components/ChatFolderListItem";
import { CreateChatFolderDialog } from "./components/CreateChatFolderDialog";

const ChatFolderListInner: React.FC = () => {
  const { t } = useTranslation("sidebar");
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { data: activeChatData } = useGetChatByUuid(chatId);
  const [scrollParentElement, setScrollParentElement] =
    useState<HTMLElement | null>(null);
  const { ref: infiniteScrollRef, inView: isInView } = useInView({
    root: scrollParentElement,
  });

  const {
    data: folders = [],
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useChatFolderList();

  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isInView]);

  const handleFolderListContentRef = useCallback(
    (element: HTMLDivElement | null): void => {
      if (!element) {
        setScrollParentElement(null);
        return;
      }

      let parentElement = element.parentElement;

      while (parentElement) {
        const { overflowY } = globalThis.getComputedStyle(parentElement);
        const canScroll =
          (overflowY === "auto" || overflowY === "scroll") &&
          parentElement.scrollHeight > parentElement.clientHeight;

        if (canScroll) {
          setScrollParentElement(parentElement);
          return;
        }

        parentElement = parentElement.parentElement;
      }

      setScrollParentElement(null);
    },
    [],
  );
  const { mutateAsync: createFolder } = useCreateChatFolder();

  const handleCreate = useCallback(
    async (name: string): Promise<void> => {
      await createFolder({ name });
    },
    [createFolder],
  );

  const handleOpenCreateDialog = useCallback((): void => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleManageFolders = useCallback((): void => {
    navigate(PATHS.CHAT_FOLDERS);
  }, [navigate]);

  return (
    <>
      {/* NOTE: NSAI-6342 — the folders used to be a collapsible section headed
          "Folders" with its two actions hidden behind a `...` that appeared on
          hover. The tab is that heading now, so the section header is gone and
          the actions it hid are a visible row instead: a member looking for
          "where do I make a folder" no longer has to hover the right six
          pixels to find out. */}
      <div
        className="flex items-center gap-1 px-1 pb-1"
        data-testid="sidebar-chat-folders"
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-8 min-w-0 flex-1 justify-start gap-2 px-1.5 text-sm font-normal text-foreground-secondary hover:text-foreground"
          onClick={handleOpenCreateDialog}
          data-testid="sidebar-new-folder-button"
        >
          <FolderPlus className="h-4 w-4 shrink-0" />
          <span className="truncate">{t("chat_folder_list.new_folder")}</span>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-foreground-secondary hover:text-foreground"
          onClick={handleManageFolders}
          aria-label={t("chat_folder_list.manage_folders")}
          title={t("chat_folder_list.manage_folders")}
          data-testid="sidebar-manage-folders-button"
        >
          <FolderCog className="h-4 w-4" />
        </Button>
      </div>
      <div ref={handleFolderListContentRef} className="w-full min-w-0 pb-1">
        {folders.length === 0 && !isFetchingNextPage && (
          <p
            className="flex h-8 items-center px-2.5 text-sm text-foreground-secondary"
            data-testid="sidebar-chat-folders-empty-state"
          >
            {t("chat_folder_list.no_folders")}
          </p>
        )}
        {folders.length > 0 && (
          <Accordion type="multiple" className="w-full">
            {folders.map((folder) => (
              <ChatFolderListItem
                key={folder.uuid}
                folder={folder}
                activeChatFolderId={activeChatData?.folderId}
              />
            ))}
          </Accordion>
        )}
        <div ref={infiniteScrollRef}>
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
        </div>
      </div>
      <CreateChatFolderDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreate={handleCreate}
      />
    </>
  );
};

export const ChatFolderList = memo(ChatFolderListInner);
